import { STANDARD_BRANCH } from '@festina-life/api'
import { notEmpty } from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo } from 'react'

import { useParameterContext } from '../../context/parameterContext'
import { useNavigationParams } from '../../system/useNavigationParams'
import { StackableTable } from '../stackableTable/StackableTable'
import type {
  StackableType,
  ValidStackableRatios,
} from '../stackableTable/types'
import { generateTableRowEntries } from './helpers'
import { ParameterValueCell } from './ParameterValueCell'
import type { ParameterValueRowData } from './types'

export const ParameterValuesTable = () => {
  const {
    parameterDefinitionOperation,
    parameterValueEntityOperationsForView,
    hasMoreValueOperations,
    parameterValueErrors,
  } = useParameterContext()

  const parameterDefinition =
    parameterDefinitionOperation?.internalOperation.entity

  const { branch } = useNavigationParams()

  const translate = useTranslate()
  const numberOfDimensions = useMemo(
    () =>
      Math.max(
        parameterDefinition?.dimensionDefinitions?.length ?? 0,
        ...(parameterValueEntityOperationsForView ?? []).map(
          (valueOperation) =>
            valueOperation.internalOperation.entity?.dimensionCount ?? 0,
        ),
      ),
    [
      parameterDefinition?.dimensionDefinitions?.length,
      parameterValueEntityOperationsForView,
    ],
  )

  const ratios: ValidStackableRatios[] = [...Array(numberOfDimensions + 1)].map(
    () => 1,
  )

  const processedOperationKeys: string[] = []
  const toStackableType = (
    valueRowData: ParameterValueRowData,
  ): StackableType<ParameterValueRowData> => {
    const operationKey = `${valueRowData.interval.from ?? 'no-date'}-${
      valueRowData.interval.to ? valueRowData.interval.to : '-'
    }`
    const isGrouped = processedOperationKeys.includes(operationKey)

    processedOperationKeys.push(operationKey)

    return {
      value: valueRowData,
      columns: 2 + numberOfDimensions,
      key: (index) => `${operationKey}/${index}`,
      ratios,
      isGrouped,
      errors: [],
      shouldShowOnOverflow: true,
    }
  }

  /**
   * TODO: Currently we assume a direct 1:1 relationship between parameter definition dimensions and dimension values
   *  regardless of versioning. We'll need to figure out how to handle versioning and adding/removing dimensions between versions
   *  in the future.
   */
  const allTableRows = generateTableRowEntries(
    parameterValueEntityOperationsForView ?? [],
    parameterDefinition?.dimensionDefinitions?.filter(notEmpty) ?? [],
  ).map(toStackableType)

  return (
    <>
      {Array.from(parameterValueErrors.values())
        .flat()
        .map(({ error }) => (
          <Alert
            key={`${error.operationKey}-${error.errorMessage}`}
            severity="error"
            title={`${error.operationName}: ${error.errorMessage}`}
            className="my-8"
          />
        ))}
      {hasMoreValueOperations && (
        <Alert
          severity="warning"
          title={
            branch && branch !== STANDARD_BRANCH
              ? translate(
                  'parameterViewer.values.moreValuesWarningWithoutExport',
                )
              : translate('parameterViewer.values.moreValuesWarning')
          }
          className="my-8"
        />
      )}
      <StackableTable
        indentationStep={0}
        emptyRowColumnRatios={ratios}
        rowClassName="min-h-12 overflow-hidden"
        headers={[
          translate('parameterViewer.values.table.header.interval'),
          ...new Array(numberOfDimensions)
            .values()
            .map(
              (_, dimensionIndex) =>
                parameterDefinition?.dimensionDefinitions?.at(dimensionIndex)
                  ?.name ?? '-',
            )
            .toArray(),
          translate('parameterViewer.values.table.header.value'),
        ]}
        data={allTableRows}
        childCell={ParameterValueCell}
      />
    </>
  )
}
